<template>
  <v-container fluid>
    <PageHeading
      :text="
        eventCategory.title
          ? `События: ${eventCategory.title}`
          : $t('pages-name.parent.events')
      "
      mode="grid"
      :grid-data-loading="gridDataLoading"
      :results-count="resultsCount"
    >
      <template v-slot:[`toolbar.prepend`]>
        <v-col cols="3">
          <v-select
            v-if="!categorySlug && eventCategories.length"
            hide-details
            dense
            outlined
            item-text="title"
            item-value="id"
            return-object
            label="Создать событие"
            :items="eventCategories"
            @change="createEvent"
          />
          <v-btn
            v-else
            link
            small
            outlined
            color="primary"
            class="mx-2 mt-2"
            :to="{
              name: 'page-event-new',
              params: { categorySlug }
            }"
          >
            <v-icon class="mr-2" small> mdi-plus </v-icon>
            Создать событие
          </v-btn>
        </v-col>
      </template>
    </PageHeading>
    <v-row class="mt-4">
      <v-col cols="4">
        <span>Фильтр событий</span>
        <v-select
          outlined
          item-text="title"
          item-valugridPageInterfacee="value"
          hide-details
          dense
          :value="filter"
          :items="$options.filtersOptional"
          @input="filter = $event"
        />
      </v-col>
      <v-col cols="4" v-if="filter === 'custom'">
        <span>Дата начала</span>
        <v-menu
          ref="menuStartDate"
          :close-on-content-click="false"
          min-width="auto"
          v-model="menuStartDate"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-row align="center" no-gutters>
              <v-text-field
                hide-details
                outlined
                readonly
                dense
                v-bind="attrs"
                :value="preparedFormatTime(startDate)"
                v-on="on"
                @change="startDate = $event"
              />
              <v-icon class="ml-2">mdi-calendar</v-icon>
            </v-row>
          </template>
          <v-date-picker
            no-title
            scrollable
            first-day-of-week="1"
            :value="startDate"
            @change="startDate = $event"
            @input="menuStartDate = false"
          >
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="4">
        <span>Дата окончания</span>
        <v-menu
          ref="menuEndDate"
          :close-on-content-click="false"
          :disabled="!startDate"
          min-width="auto"
          v-model="menuEndDate"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-row align="center" no-gutters>
              <v-text-field
                hide-details
                outlined
                readonly
                dense
                v-bind="attrs"
                :value="preparedFormatTime(endDate)"
                v-on="on"
                @change="endDate = $event"
              />
              <v-icon class="ml-2">mdi-calendar</v-icon>
            </v-row>
          </template>
          <v-date-picker
            no-title
            scrollable
            first-day-of-week="1"
            :min="startDate"
            :value="endDate"
            @change="endDate = $event"
            @input="menuEndDate = false"
          >
          </v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <GridEvents ref="grid" :category="eventCategory" :filter="getFilter" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import gridPageInterface from '@/interfaces/grid-page-interface-mixin'

import dayjs from 'dayjs'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import { defaultDateFormat } from '@/configs/date'

import GridEvents from './grid'
import PageHeading from '@/components/page-heading'

import eventCategoryQuery from '@/services/gql/types/EventCategory/queries/eventCategory.gql'
import eventCategoriesQuery from '@/services/gql/types/EventCategories/queries/eventCategories.gql'

dayjs.extend(quarterOfYear)
const filtersOptional = [
  {
    title: 'С текущего дня',
    value: dayjs().startOf('day').utc(true).format()
  },
  {
    title: 'С начала месяца',
    value: dayjs().startOf('month').utc(true).format()
  },
  {
    title: 'С начала квартала',
    value: dayjs().startOf('quarter').utc(true).format()
  },
  {
    title: 'С начала года',
    value: dayjs().startOf('year').utc(true).format()
  },
  {
    title: 'Произвольно',
    value: 'custom'
  }
]

export default {
  name: 'PageEvents',
  filtersOptional,
  props: {
    categorySlug: {
      type: [Number, String],
      default: null
    }
  },
  components: {
    PageHeading,
    GridEvents
  },
  metaInfo () {
    return {
      title: this.eventCategory.title
        ? `События: ${this.eventCategory.title || ''}`
        : 'Список событий'
    }
  },
  mixins: [gridPageInterface],
  data () {
    return {
      eventCategory: {},
      totalCount: 0,
      eventCategories: [],
      filter: filtersOptional[0].value,
      startDate: dayjs().startOf('day').utc(true).format(),
      endDate: null,
      menuStartDate: false,
      menuEndDate: false
    }
  },
  computed: {
    getFilter () {
      if (this.filter === 'custom') {
        if (this.endDate) {
          return {
            from: dayjs(this.startDate).startOf('day').utc(true).format(),
            to: dayjs(this.endDate).endOf('day').utc(true).format()
          }
        } else if (this.startDate) {
          return {
            from: dayjs(this.startDate).startOf('day').utc(true).format()
          }
        } else return {}
      }
      return {
        from: this.filter
      }
    }
  },
  watch: {
    categorySlug (data) {
      if (data === null) this.eventCategory = {}
    },
    startDate (newDate) {
      if (newDate > this.endDate) this.endDate = null
    }
  },
  methods: {
    createEvent (eventCategory) {
      this.$router.push({
        name: 'page-event-new',
        params: {
          categorySlug: eventCategory.slug
        }
      })
    },
    preparedFormatTime (value) {
      if (value !== null) {
        return dayjs(value || new Date())
          .format(defaultDateFormat)
      }
      return value
    }
  },
  apollo: {
    eventCategories: {
      fetchPolicy: 'network-only',
      query: eventCategoriesQuery,
      variables () {
        return {
          where: {}
        }
      },
      result ({ data }) {
        this.eventCategories = data.eventCategories.list.filter(category => category?.slug !== 'movies')
      }
    },
    eventCategory: {
      fetchPolicy: 'network-only',
      query: eventCategoryQuery,
      pollInterval: 30000,
      skip () {
        return !this.categorySlug
      },
      variables () {
        return {
          where: {
            slug: this.categorySlug
          }
        }
      }
    }
  }
}
</script>
